import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import { captureException } from "@sentry/react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { fillLabels, type LidarClearanceLabel } from "./labelsApi";
import type { LidarClearanceAxis } from "./schemas";
import t from "./translations";

type Props = {
  open: boolean;
  axes: LidarClearanceAxis[];
  region: string;
  systemId: number;
  onClose: () => void;
  onFilled: (labels: LidarClearanceLabel[]) => void;
};

export default function FillLabelsDialog({
  open,
  axes,
  region,
  systemId,
  onClose,
  onFilled,
}: Props) {
  const [selectedAxisIds, setSelectedAxisIds] = useState<Set<number>>(
    new Set()
  );
  const [filling, setFilling] = useState(false);

  useEffect(() => {
    if (open) setSelectedAxisIds(new Set());
  }, [open]);

  const axisIds = [...selectedAxisIds];
  const rawCombinationCount = axisIds.reduce((count, axisId) => {
    const axis = axes.find((candidate) => candidate.id === axisId);
    return axis ? count * (axis.max_value - axis.min_value + 1) : count;
  }, 1);
  // Every axis at 0 is skipped server-side - it's already the implicit
  // default for any point no label mentions, so it never gets its own row.
  const combinationCount = Math.max(rawCombinationCount - 1, 0);
  const canFill = axisIds.length > 0 && !filling;

  const toggleAxis = (axisId: number, checked: boolean) => {
    setSelectedAxisIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(axisId);
      else next.delete(axisId);
      return next;
    });
  };

  const handleFill = async () => {
    setFilling(true);
    try {
      const created = await fillLabels(region, systemId, axisIds);
      onFilled(created);
      onClose();
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.fillLabels" });
      toast.error(t.FailedToFillLabels);
    } finally {
      setFilling(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => (filling ? undefined : onClose())} fullWidth>
      <DialogTitle>{t.FillLabelsDialogTitle}</DialogTitle>
      <DialogContent>
        <FormGroup>
          {axes.map((axis) => (
            <FormControlLabel
              key={axis.id}
              control={
                <Checkbox
                  checked={selectedAxisIds.has(axis.id)}
                  onChange={(event) =>
                    toggleAxis(axis.id, event.target.checked)
                  }
                  disabled={filling}
                />
              }
              label={axis.name_en}
            />
          ))}
        </FormGroup>
        {axisIds.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            {t.FillCombinationCountHint(combinationCount)}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={filling}>
          {t.CancelButton}
        </Button>
        <Button
          onClick={handleFill}
          variant="contained"
          disabled={!canFill}
          startIcon={
            filling ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {t.FillButton}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
